import {
  SelectInput,
  TextArea,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { StateDisclaimer } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { useCallback, useState } from "react";
import { prohibitedContentValidation } from "../utils/validation";
import "./StatePanel.scss";

interface StatePanelProps {
  states: string[];
  stateDisclaimers?: StateDisclaimer[];
  onDisclaimersChanged?: (disclaimers: StateDisclaimer[]) => void;
  changeError?: (id: string, status: boolean) => void;
}

export const getStateOptions = (states: string[]) => {
  return states
    ? states.map(state => ({
        disabled: false,
        label: `${state}`,
        value: state
      }))
    : [];
};

export const getStateDisclaimer = (
  state: string,
  stateDisclaimers: StateDisclaimer[]
): string => {
  if (!stateDisclaimers) return "";
  const disclaimer = stateDisclaimers.find(
    dis => dis.state.toLowerCase() === state.toLowerCase()
  );
  return disclaimer ? disclaimer.disclaimer : "";
};

export const StatePanel = ({
  states,
  stateDisclaimers,
  onDisclaimersChanged,
  changeError = (id: string, status: boolean) => {
    /* no-op */
  }
}: StatePanelProps) => {
  const [state, setState] = useState<string>(
    stateDisclaimers?.[0]?.state || ""
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const handleChangeError = useCallback(
    (id: string, status: boolean) => {
      changeError(id, status);
      setFieldErrors(prev => ({ ...prev, [id]: status }));
    },
    [changeError]
  );

  const hasErrors = Object.values(fieldErrors).some(error => error === true);
  return (
    <div className="state-panel">
      <Typography variant="h4">
        {Locale.getText("STATE_DISCLAIMERS") as string}
      </Typography>
      <SelectInput
        disabled={hasErrors}
        displayDeselectOption={false}
        id="for-pref-states"
        label={Locale.getText("STATE") as string}
        name={"disclaimer-state"}
        options={getStateOptions(states)}
        value={state}
        onChange={event => {
          setState(event.target.value as string);
        }}
      />
      <TextArea
        getError={prohibitedContentValidation(
          "state-disclaimer-prohibited",
          getStateDisclaimer(state, stateDisclaimers || []),
          handleChangeError
        )}
        label={Locale.getText("DISCLAIMER") as string}
        name={"disclaimer-description"}
        rows={4}
        value={getStateDisclaimer(state, stateDisclaimers || [])}
        onChange={event => {
          if (onDisclaimersChanged) {
            const clonedDisclaimers =
              stateDisclaimers?.map(disclaimer => {
                if (disclaimer.state.toLowerCase() === state.toLowerCase()) {
                  return {
                    ...disclaimer,
                    disclaimer: event.target.value
                  };
                }
                return disclaimer;
              }) || [];
            onDisclaimersChanged(clonedDisclaimers);
          }
        }}
      />
    </div>
  );
};
